export interface BookmarkItem {
  id: number;
  sectionId: string;
  sectionTitle: string;
  parentSections: string[];
  articleSlug?: string | null;
}

interface SubSectionGroup {
  subSectionTitle: string;
  bookmarks: BookmarkItem[];
}

export interface BookmarkGroupEntry {
  type: "bookmark_group";
  bookmarks: BookmarkItem[];
}

export interface SubSectionEntry {
  type: "subSection";
  subSection: SubSectionGroup;
}

interface SectionGroup {
  sectionTitle: string;
  entries: (BookmarkGroupEntry | SubSectionEntry)[];
}

export interface SectionEntry {
  type: "section";
  section: SectionGroup;
}

export interface ChapterGroup {
  chapterTitle: string;
  entries: (BookmarkGroupEntry | SectionEntry)[];
}

export const sectionIdOrder = (sectionId: string): number[] =>
  sectionId.split("-").flatMap((segment) => {
    const match = segment.match(/:(\d+)$/);
    return match ? [Number(match[1])] : [];
  });

export const compareSectionIds = (a: string, b: string): number => {
  const aNums = sectionIdOrder(a);
  const bNums = sectionIdOrder(b);
  const maxLength = Math.max(aNums.length, bNums.length);

  for (let i = 0; i < maxLength; i++) {
    const aVal = aNums[i] ?? -1;
    const bVal = bNums[i] ?? -1;
    if (aVal !== bVal) return aVal - bVal;
  }
  return 0;
};

export const buildChapterGroups = (
  bookmarks: BookmarkItem[],
  publicationTitle: string,
): ChapterGroup[] => {
  const sorted = [...bookmarks].sort((a, b) =>
    compareSectionIds(a.sectionId, b.sectionId),
  );
  const chapters = new Map<string, ChapterGroup>();

  const addBookmarkToEntries = (
    entries: (BookmarkGroupEntry | SectionEntry | SubSectionEntry)[],
    b: BookmarkItem,
  ) => {
    const lastEntry = entries[entries.length - 1];
    if (lastEntry?.type === "bookmark_group") {
      lastEntry.bookmarks.push(b);
    } else {
      entries.push({ type: "bookmark_group", bookmarks: [b] });
    }
  };

  const sectionMap = new Map<string, Map<string, SectionEntry>>();
  const subSectionMap = new Map<string, Map<string, SubSectionEntry>>();

  for (const bookmark of sorted) {
    const [chapterName, sectionName, subSectionName] = bookmark.parentSections;
    const chapterTitle = chapterName || publicationTitle;

    if (!chapters.has(chapterTitle)) {
      chapters.set(chapterTitle, { chapterTitle, entries: [] });
      sectionMap.set(chapterTitle, new Map());
    }
    const chapterGroup = chapters.get(chapterTitle)!;

    const isDirectChapter =
      !sectionName ||
      bookmark.sectionTitle === chapterTitle ||
      bookmark.sectionTitle === sectionName;

    if (isDirectChapter) {
      addBookmarkToEntries(chapterGroup.entries, bookmark);
      continue;
    }

    const chapterSections = sectionMap.get(chapterTitle)!;
    let sectionEntry = chapterSections.get(sectionName);

    if (!sectionEntry) {
      sectionEntry = {
        type: "section",
        section: { sectionTitle: sectionName, entries: [] },
      };
      chapterSections.set(sectionName, sectionEntry);
      chapterGroup.entries.push(sectionEntry);

      const sectionKey = `${chapterTitle}|||${sectionName}`;
      subSectionMap.set(sectionKey, new Map());
    }

    const isDirectSection =
      !subSectionName ||
      bookmark.sectionTitle === sectionName ||
      bookmark.sectionTitle === subSectionName;

    if (isDirectSection) {
      addBookmarkToEntries(sectionEntry.section.entries, bookmark);
      continue;
    }

    const sectionKey = `${chapterTitle}|||${sectionName}`;
    const sectionSubSections = subSectionMap.get(sectionKey)!;
    let subSectionEntry = sectionSubSections.get(subSectionName);

    if (!subSectionEntry) {
      subSectionEntry = {
        type: "subSection",
        subSection: { subSectionTitle: subSectionName, bookmarks: [] },
      };
      sectionSubSections.set(subSectionName, subSectionEntry);
      sectionEntry.section.entries.push(subSectionEntry);
    }
    subSectionEntry.subSection.bookmarks.push(bookmark);
  }

  return Array.from(chapters.values());
};
export const FRJ = "FRJ"; // Frj articles don't have a publicationId, so we group them under this fake publicationId
export type FRJOrUnknownPublication = { id: number; title: string };
export const frjPublication: FRJOrUnknownPublication = { id: -1, title: FRJ };
export const unknownPublicationTitle = "Unknown Publication";
export const unknownPublication: FRJOrUnknownPublication = {
  id: -2,
  title: unknownPublicationTitle,
};
